var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import getCustomLegend from "utils/ChartHelpers.js/CustomLegendPlugin";
import { cn } from "utils/common.utils";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
ChartJS.register(ArcElement, Tooltip, Legend);
/**
 * Custom Hook to configure Chart.js Doughnut options and return ready-to-use components.
 * This hook generates options immutably and memoizes the result for performance.
 *
 * @param {string} containerId - ID of the container element where the custom HTML legend will be placed.
 * @param {boolean} [isHalfChart=false] - If true, configures the chart for a half-doughnut display (rotation 180 deg).
 * @returns {{
 * DoughnutChart: function(props: {data: object}): JSX.Element,
 * DoughnutChartFooter: function(props: {className?: string}): JSX.Element
 * }} An object containing the configured Chart component and its corresponding legend container element.
 */
export function useDoughnutChart(containerId, isHalfChart) {
    if (isHalfChart === void 0) { isHalfChart = false; }
    // Use useMemo to ensure the options object is only re-created
    // when the dependencies (containerId or isHalfChart) change.
    var options = useMemo(function () {
        // --- 1. Define Base Configuration ---
        var baseCommonOptions = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: "80%",
            radius: "100%",
            borderRadius: 1,
            borderJoinStyle: "miter",
            borderWidth: 0,
            spacing: 0,
            borderAlign: "center",
        };
        var basePlugins = {
            htmlLegend: {
                containerID: containerId,
            },
            legend: {
                display: false,
            },
            // Base tooltip setting, safely checking document direction
            // Also set color defaults (can be overridden via CSS variables)
            tooltip: {
                ltr: typeof document !== "undefined" && document.body.dir === "rtl"
                    ? false
                    : true,
            },
        };
        // --- 2. Create Initial Options Object (Full Copy) ---
        var finalOptions = __assign(__assign({}, baseCommonOptions), { plugins: __assign({}, basePlugins) });
        // --- 3. Apply Half-Chart Specific Overrides (Immutable Modification) ---
        if (isHalfChart) {
            finalOptions.rotation = -90;
            finalOptions.circumference = 180;
            finalOptions.cutout = "85%";
            finalOptions.radius = "100%";
            // Deeply clone and override the tooltip filter
            finalOptions.plugins = __assign(__assign({}, finalOptions.plugins), { tooltip: __assign(__assign({}, finalOptions.plugins.tooltip), { filter: function (item) {
                        return (item === null || item === void 0 ? void 0 : item.dataIndex) === 0;
                    } }) });
        }
        return finalOptions;
    }, [containerId, isHalfChart]);
    var _hoverOptions = {
        plugins: {
            tooltip: {
                // disable built-in tooltip rendering — we render externally
                enabled: false,
                displayColors: false,
                // keep the label callback to format "Label: value"
                callbacks: {
                    label: function (context) {
                        var label = context.label || "";
                        var value = context.parsed !== undefined ? context.parsed : context.raw;
                        return "".concat(label, ": ").concat(value);
                    },
                },
                // external tooltip renderer (hardcoded colors, fully opaque background)
                external: function (context) {
                    var chart = context.chart, tooltip = context.tooltip;
                    var canvasParent = chart.canvas && chart.canvas.parentElement;
                    if (!canvasParent)
                        return;
                    // ensure parent is positioned for absolute placement
                    if (!canvasParent.style.position)
                        canvasParent.style.position = "relative";
                    var tooltipEl = canvasParent.querySelector('.chartjs-external-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.className = 'chartjs-external-tooltip';
                        // Fully opaque grey background + white text
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.background = 'rgba(0,0,0,0.8)'; // fully opaque (alpha = 1)
                        tooltipEl.style.color = '#ffffff';
                        tooltipEl.style.padding = '6px 8px';
                        tooltipEl.style.borderRadius = '6px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.whiteSpace = 'nowrap';
                        tooltipEl.style.transition = 'opacity 0.12s ease, transform 0.12s ease';
                        tooltipEl.style.zIndex = '9999';
                        tooltipEl.style.border = '1px solid rgba(255,255,255,0.08)';
                        tooltipEl.style.boxShadow = '0 6px 18px rgba(0,0,0,0.5)';
                        // create an arrow element inside the tooltip
                        var arrowEl_1 = document.createElement('div');
                        arrowEl_1.className = 'chartjs-external-tooltip-arrow';
                        arrowEl_1.style.position = 'absolute';
                        arrowEl_1.style.width = '0px';
                        arrowEl_1.style.height = '0px';
                        arrowEl_1.style.pointerEvents = 'none';
                        tooltipEl.appendChild(arrowEl_1);
                        canvasParent.appendChild(tooltipEl);
                    }
                    // Hide when tooltip is not visible
                    if (!tooltip || tooltip.opacity === 0) {
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.visibility = 'hidden';
                        return;
                    }
                    // Build tooltip content using formatted body lines
                    var lines = [];
                    if (tooltip.body) {
                        tooltip.body.forEach(function (bodyItem) {
                            var text = bodyItem.lines.join(' ');
                            lines.push(text);
                        });
                    }
                    tooltipEl.innerHTML = lines.map(function (l) { return "<div>".concat(l, "</div>"); }).join('');
                    // Position tooltip near the caret position with smart left/right placement
                    var caretX = tooltip.caretX || chart.width / 2;
                    var caretY = tooltip.caretY || chart.height / 2;
                    // reset to measure
                    tooltipEl.style.opacity = '1';
                    tooltipEl.style.visibility = 'visible';
                    tooltipEl.style.left = '0px';
                    tooltipEl.style.top = '0px';
                    var elRect = tooltipEl.getBoundingClientRect();
                    var parentRect = canvasParent.getBoundingClientRect();
                    var arrowSize = 6;
                    var arrowOffset = 8; // space between tooltip and caret
                    // Calculate available space on left and right
                    var spaceOnRight = parentRect.width - caretX;
                    var spaceOnLeft = caretX;
                    var tooltipWidth = elRect.width + arrowSize;
                    var tooltipHeight = elRect.height;
                    // Choose placement based on available space
                    var left, placedRight;
                    if (spaceOnRight >= tooltipWidth + arrowOffset) {
                        // Place on right side
                        left = caretX + arrowOffset;
                        placedRight = true;
                    }
                    else if (spaceOnLeft >= tooltipWidth + arrowOffset) {
                        // Place on left side
                        left = caretX - tooltipWidth - arrowOffset;
                        placedRight = false;
                    }
                    else {
                        // Fallback: place where there's more space
                        if (spaceOnRight >= spaceOnLeft) {
                            left = Math.min(caretX + arrowOffset, parentRect.width - tooltipWidth);
                            placedRight = true;
                        }
                        else {
                            left = Math.max(caretX - tooltipWidth - arrowOffset, 0);
                            placedRight = false;
                        }
                    }
                    // Vertical positioning: center on caret, with bounds checking
                    var top = caretY - tooltipHeight / 2;
                    if (top < 4)
                        top = 4;
                    if (top + tooltipHeight > parentRect.height)
                        top = parentRect.height - tooltipHeight - 4;
                    tooltipEl.style.left = "".concat(Math.round(left), "px");
                    tooltipEl.style.top = "".concat(Math.round(top), "px");
                    // position and style arrow inside tooltip
                    var arrowEl = tooltipEl.querySelector('.chartjs-external-tooltip-arrow');
                    if (arrowEl) {
                        // clear previous borders
                        arrowEl.style.borderTop = '';
                        arrowEl.style.borderBottom = '';
                        arrowEl.style.borderLeft = '';
                        arrowEl.style.borderRight = '';
                        arrowEl.style.left = 'auto';
                        arrowEl.style.right = 'auto';
                        if (placedRight) {
                            // left-pointing triangle (arrow points back to caret on the left)
                            arrowEl.style.left = "-".concat(arrowSize, "px");
                            arrowEl.style.borderTop = "".concat(arrowSize, "px solid transparent");
                            arrowEl.style.borderBottom = "".concat(arrowSize, "px solid transparent");
                            arrowEl.style.borderRight = "".concat(arrowSize, "px solid rgba(75,85,99,1)");
                        }
                        else {
                            // right-pointing triangle (arrow points back to caret on the right)
                            arrowEl.style.right = "-".concat(arrowSize, "px");
                            arrowEl.style.borderTop = "".concat(arrowSize, "px solid transparent");
                            arrowEl.style.borderBottom = "".concat(arrowSize, "px solid transparent");
                            arrowEl.style.borderLeft = "".concat(arrowSize, "px solid rgba(75,85,99,1)");
                        }
                        // Vertical center of arrow
                        arrowEl.style.top = "".concat(Math.round(tooltipHeight / 2 - arrowSize), "px");
                    }
                },
            },
        },
    };
    return {
        DoughnutChart: function (_a) {
            var data = _a.data, overrideOptions = _a.options;
            var _overrideOptions = overrideOptions || _hoverOptions || {};
            var mergedOptions = __assign(__assign(__assign({}, options), (_overrideOptions || {})), { plugins: __assign(__assign({}, (options.plugins || {})), ((_overrideOptions && _overrideOptions.plugins) || {})) });
            return (_jsx(Doughnut, { data: data, options: mergedOptions, plugins: [getCustomLegend()] }));
        },
        DoughnutChartFooter: function (_a) {
            var className = _a.className;
            return (_jsx("div", { id: containerId, className: cn("mt-8", className) }));
        },
    };
}
